import { useSubjectMember } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { useMemo } from 'react'

import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { renderText } from '@/modules/shared/helpers/text-rendering'
import { QuestionnaireHeroContent } from '../../components/questionnaire-hero-content/QuestionnaireHeroContent'
import { splitText } from '../../utils/split-question-text'

import type { IQuestionnaireQuestion, QuestionAnswerInput } from '@healthblocks-io/core'

type Props = {
  question: IQuestionnaireQuestion | null
  onSubmitAnswer: (answer: QuestionAnswerInput) => void
  onGoBack: () => void
  isSaving?: boolean
}

/**
 * The completed page for the questionnaire
 *  - Is shown when the last question is a display question
 * @param question - The questionnaire question data
 * @param onSubmitAnswer - The function to call when the user wants to submit the answer
 * @param onComplete - The function to call when the user wants to complete the questionnaire
 * @param onGoBack - The function to call when the user wants to go back
 * @param onGoNext - The function to call when the user wants to go next
 * @returns
 */
export const QuestionnaireCompleted = ({ question, onSubmitAnswer, onGoBack, isSaving }: Props) => {
  const subjectMember = useSubjectMember()

  const [, description] = useMemo(() => splitText(question?.text), [question?.text])

  const descriptionElement = useMemo(() => {
    return renderText(description)
  }, [description])

  const handleSubmitAnswer = () => {
    if (!question) return

    onSubmitAnswer({
      linkId: question.linkId ?? question.id,
      value: 'seen',
    })
  }

  return (
    <QuestionnaireHeroContent
      title={t({
        id: 'questionnaire.success.title',
        comment: 'Title at questionnaire success page',
        message: 'Bedankt, {name}',
        values: {
          name: subjectMember?.givenName,
        },
      })}
      description={descriptionElement ?? description}
      image={'/img/questionnaire-completed.jpg'}
      onGoBack={onGoBack}
      buttons={
        <>
          <Button variant={'Primary'} onClick={handleSubmitAnswer} isLoading={isSaving}>
            {t({
              id: 'globals.submit',
              comment: 'Global for submit',
              message: 'Verzenden',
            })}
          </Button>
        </>
      }
    />
  )
}
